import React, { useState } from 'react';
import axios from 'axios';
import { CreditCard, Upload, MessageSquare, CheckCircle, Loader, X } from 'lucide-react';

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  surface: '#0D1120', surf2: '#111827', surf3: '#161E30',
  border: 'rgba(255,255,255,0.07)', bord2: 'rgba(255,255,255,0.11)',
  text: '#E2E8F0', muted: 'rgba(255,255,255,0.35)',
  blue: '#3B82F6', teal: '#22D3A5', red: '#F87171',
  font: "'DM Sans',sans-serif",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes modalIn { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }

  .mpm-input, .mpm-select, .mpm-textarea {
    width:100%; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.10);
    border-radius:9px; color:#E2E8F0; font-size:13px; font-family:'DM Sans',sans-serif;
    outline:none; transition:border-color 0.15s,background 0.15s;
  }
  .mpm-input, .mpm-select { padding:9px 12px 9px 34px; height:40px; }
  .mpm-textarea { padding:9px 12px 9px 34px; resize:vertical; min-height:72px; }
  .mpm-input::placeholder,.mpm-textarea::placeholder { color:rgba(255,255,255,0.22); }
  .mpm-input:focus,.mpm-select:focus,.mpm-textarea:focus { border-color:rgba(59,130,246,0.55); background:rgba(59,130,246,0.05); }
  .mpm-select { appearance:none; cursor:pointer; color-scheme:dark; }
  .mpm-select option { background:#111827; color:#E2E8F0; }
  .mpm-label { display:block; font-size:10px; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:rgba(255,255,255,0.38); margin-bottom:5px; }

  /* file input */
  .mpm-file-zone {
    width:100%; padding:14px 16px; border-radius:9px;
    border:1.5px dashed rgba(255,255,255,0.15); background:rgba(255,255,255,0.03);
    cursor:pointer; transition:all 0.15s; display:flex; align-items:center; gap:10px;
    color:rgba(255,255,255,0.35); font-size:12px; font-family:'DM Sans',sans-serif;
  }
  .mpm-file-zone:hover { border-color:rgba(59,130,246,0.45); background:rgba(59,130,246,0.04); color:#60A5FA; }
  .mpm-file-input { position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; }

  /* buttons */
  .mpm-btn-submit { display:inline-flex; align-items:center; gap:7px; padding:9px 20px; border-radius:9px; border:none; background:linear-gradient(135deg,#3B82F6,#22D3A5); color:#fff; font-family:'DM Sans',sans-serif; font-size:13px; font-weight:700; cursor:pointer; box-shadow:0 4px 14px rgba(59,130,246,0.28); transition:opacity 0.15s; }
  .mpm-btn-submit:hover:not(:disabled) { opacity:.88; }
  .mpm-btn-submit:disabled { opacity:.45; cursor:not-allowed; }
  .mpm-btn-cancel { display:inline-flex; align-items:center; gap:6px; padding:9px 16px; border-radius:9px; border:1px solid rgba(255,255,255,0.10); background:rgba(255,255,255,0.05); color:rgba(255,255,255,0.45); font-family:'DM Sans',sans-serif; font-size:13px; font-weight:600; cursor:pointer; transition:all 0.15s; }
  .mpm-btn-cancel:hover { background:rgba(255,255,255,0.09); color:rgba(255,255,255,0.65); }
`;

const Field = ({ label, icon: Icon, iconColor = T.muted, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label className="mpm-label">{label}</label>
    <div style={{ position: 'relative' }}>
      {Icon && <Icon size={13} color={iconColor} style={{ position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',zIndex:1 }} />}
      {children}
    </div>
  </div>
);
const TextareaField = ({ label, icon: Icon, iconColor = T.muted, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label className="mpm-label">{label}</label>
    <div style={{ position: 'relative' }}>
      {Icon && <Icon size={13} color={iconColor} style={{ position:'absolute',left:11,top:11,pointerEvents:'none',zIndex:1 }} />}
      {children}
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════
const MarkPaidModal = ({ show, onHide, salesOrderId, onSuccess }) => {
  const [paymentMethod, setPaymentMethod] = useState('');
  const [screenshot, setScreenshot]       = useState(null);
  const [comment, setComment]             = useState('');
  const [loading, setLoading]             = useState(false);

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append('paymentMethod', paymentMethod);
    formData.append('comment', comment);
    if (screenshot) formData.append('screenshot', screenshot);

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/sales/${salesOrderId}/mark-paid/`, formData);
      onSuccess();
      onHide();
    } catch (err) {
      alert('Failed to mark as paid.');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <style>{STYLES}</style>
      {/* Overlay */}
      <div
        style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.72)', backdropFilter:'blur(4px)', zIndex:1050, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
        onClick={onHide}
      >
        {/* Modal */}
        <div
          style={{ background:T.surface, border:`1px solid ${T.bord2}`, borderRadius:16, width:'100%', maxWidth:440, boxShadow:'0 24px 60px rgba(0,0,0,0.7)', animation:'modalIn 0.2s ease', fontFamily:T.font }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:9 }}>
              <div style={{ width:28,height:28,borderRadius:8,background:'rgba(34,211,165,0.12)',border:'1px solid rgba(34,211,165,0.25)',display:'flex',alignItems:'center',justifyContent:'center' }}>
                <CreditCard size={14} color={T.teal} />
              </div>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:T.text }}>Mark as Paid</div>
                <div style={{ fontSize:10, color:T.muted }}>Sales Order #{salesOrderId}</div>
              </div>
            </div>
            <button
              onClick={onHide}
              style={{ background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.10)',borderRadius:7,width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:T.muted }}
            >
              <X size={14} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding:'20px 20px 4px' }}>
            <Field label="Payment Method" icon={CreditCard} iconColor={T.teal}>
              <select
                className="mpm-select"
                value={paymentMethod}
                onChange={e => setPaymentMethod(e.target.value)}
              >
                <option value="">Select method</option>
                <option value="Cash">Cash on Delivery</option>
                <option value="IBFT">IBFT</option>
                <option value="Pay Later">Pay Later</option>
              </select>
            </Field>

            {/* File upload */}
            <div style={{ marginBottom: 16 }}>
              <label className="mpm-label">Payment Screenshot (optional)</label>
              <div style={{ position: 'relative' }}>
                <label className="mpm-file-zone">
                  <input
                    type="file"
                    className="mpm-file-input"
                    onChange={e => setScreenshot(e.target.files[0])}
                  />
                  <Upload size={14} />
                  <span>{screenshot ? screenshot.name : 'Click to upload screenshot…'}</span>
                </label>
              </div>
            </div>

            <TextareaField label="Comment (optional)" icon={MessageSquare} iconColor={T.muted}>
              <textarea
                className="mpm-textarea"
                rows={3}
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Optional notes about payment…"
              />
            </TextareaField>
          </div>

          {/* Footer */}
          <div style={{ padding:'12px 20px 18px', display:'flex', justifyContent:'flex-end', gap:8 }}>
            <button className="mpm-btn-cancel" onClick={onHide}>Cancel</button>
            <button className="mpm-btn-submit" onClick={handleSubmit} disabled={loading}>
              {loading
                ? <><Loader size={13} style={{ animation:'spin 0.8s linear infinite' }} /> Submitting…</>
                : <><CheckCircle size={13} /> Submit</>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MarkPaidModal;